import style from "./ai-feedback.module.css";
import tracking from "frontend/tracking";
import { atom, useAtom, useSetAtom } from "jotai";
import { useState, useEffect, useRef, useCallback } from "react";
import { CloseIcon } from "frontend/icons/close";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";

type AIFeedbackContextKey = "ai-generator";

type AIFeedbackContext = Record<
  AIFeedbackContextKey,
  {
    prompt: string;
    featureName: string;
  } | null
>;

const aiFeedBackContextAtom = atom<AIFeedbackContext>({} as AIFeedbackContext);

export function useSetAiFeedBackContext(contextKey: AIFeedbackContextKey) {
  const isAIFeedbackEnabled = useFeatureFlag("ai-feedback");
  const setAiFeedBackContext = useSetAtom(aiFeedBackContextAtom);
  const setAIFeedbackContextByKey = useCallback(
    (prompt: string, featureName: string) => {
      if (!isAIFeedbackEnabled) return;
      setAiFeedBackContext((previousAiFeedBackContext: AIFeedbackContext) => ({
        ...previousAiFeedBackContext,
        [contextKey]: { prompt, featureName },
      }));
    },
    [setAiFeedBackContext, contextKey, isAIFeedbackEnabled]
  );
  const clearContext = useCallback(() => {
    if (!isAIFeedbackEnabled) return;
    setAiFeedBackContext((previousAiFeedBackContext: AIFeedbackContext) => {
      const { [contextKey]: _, ...rest } = previousAiFeedBackContext;
      return { ...rest } as AIFeedbackContext;
    });
  }, [setAiFeedBackContext, contextKey, isAIFeedbackEnabled]);
  return { setAIFeedbackContextByKey, clearContext };
}

function useAIFeedback(contextKey: AIFeedbackContextKey) {
  const [aiFeedBackContext, setAiFeedBackContext] = useAtom(aiFeedBackContextAtom);
  const context = aiFeedBackContext[contextKey];
  if (!context) return null;
  const { prompt, featureName } = context;

  const handleThumbsUp = () => {
    tracking.trackAnalyticsEvent("ai_positive_feedback", {
      source: contextKey,
      featureName,
      prompt,
    });
  };

  const handleThumbsDown = () => {
    tracking.trackAnalyticsEvent("ai_negative_feedback", {
      source: contextKey,
      featureName,
      prompt,
    });
  };

  const clearContext = () => {
    setAiFeedBackContext({ ...aiFeedBackContext, [contextKey]: null });
  };

  return { prompt, featureName, handleThumbsUp, handleThumbsDown, clearContext };
}

interface AIFeedbackProps {
  contextKey: AIFeedbackContextKey;
}

export function AIFeedback({ contextKey }: AIFeedbackProps) {
  const isAIFeedbackEnabled = useFeatureFlag("ai-feedback");
  const aiFeedbackState = useAIFeedback(contextKey);
  const clearContextRef = useRef<() => void>(() => {});
  const [showThankYou, setShowThankYou] = useState(false);

  clearContextRef.current = aiFeedbackState?.clearContext || (() => {});

  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        setShowThankYou(false);
        aiFeedbackState?.clearContext();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showThankYou, aiFeedbackState]);

  useEffect(() => {
    return () => {
      clearContextRef.current();
    };
  }, []);

  if (!aiFeedbackState) return null;

  const { handleThumbsUp, handleThumbsDown, featureName } = aiFeedbackState;

  const handleClick = (handler: () => void) => {
    handler();
    setShowThankYou(true);
  };

  const handleClose = () => {
    tracking.trackAnalyticsEvent("ai_feedback_dismissed", {
      source: contextKey,
      featureName,
    });
    aiFeedbackState.clearContext();
  };

  if (!isAIFeedbackEnabled) return null;

  return (
    <div className={style.modal}>
      <CloseIcon color="#848199" className={style.closeButtonIcon} onClick={handleClose} />
      {showThankYou ? (
        <div className={style.thankYouContainer}>
          <p className={style.thankYouMessage}>Thank you for your feedback! 🙏</p>
        </div>
      ) : (
        <>
          <p className={style.question}>Was this template useful?</p>
          <div className={style.ratingContainer}>
            <button className={style.thumbsButton} onClick={() => handleClick(handleThumbsUp)} aria-label="Thumbs up">
              👍
            </button>
            <button
              className={style.thumbsButton}
              onClick={() => handleClick(handleThumbsDown)}
              aria-label="Thumbs down"
            >
              👎
            </button>
          </div>
        </>
      )}
    </div>
  );
}
